import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import '../styles/ExpenseForm.css';

const SupervisorExpenseForm = ({ onExpenseAdded, onCancel }) => {
  const { user } = useAuth();
  const [businessDateTime, setBusinessDateTime] = useState(null);
  const [formData, setFormData] = useState({
    expense_date: new Date().toISOString().split('T')[0], // Fecha actual por defecto
    expense_amount: '',
    expense_description: '',
    payment_method: 'efectivo'.toLowerCase(), // Valor por defecto en minúsculas
    point_of_sale_id: '',
    expense_category: '',
    expense_type_id: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pointsOfSale, setPointsOfSale] = useState([]);
  const [loadingPointsOfSale, setLoadingPointsOfSale] = useState(true);
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [expenseTypes, setExpenseTypes] = useState([]);
  const [loadingExpenseTypes, setLoadingExpenseTypes] = useState(false);
  const [supervisorInfo, setSupervisorInfo] = useState(null);
  
  // Función para obtener la fecha y hora del negocio desde el servidor
  const fetchBusinessTime = async (businessId) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No se encontró token de autenticación');
        return null;
      }

      const response = await axios.get(`/api/sales/server-time?business_id=${businessId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.data && response.data.businessTime) {
        console.log('Fecha/hora del negocio obtenida:', response.data.businessTime);
        return response.data.businessTime;
      } else {
        console.error('No se recibió fecha/hora del negocio en la respuesta');
        return null;
      }
    } catch (error) {
      console.error('Error al obtener fecha/hora del negocio:', error);
      return null;
    }
  };

  // Obtener información del supervisor
  useEffect(() => {
    const fetchSupervisorInfo = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setError('No se encontró un token de autenticación. Por favor, inicie sesión nuevamente.');
          return;
        }

        const response = await axios.get('/api/supervisors/info', {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        setSupervisorInfo(response.data);
      } catch (error) {
        console.error('Error al obtener información del supervisor:', error);
        setError('Error al cargar información del supervisor. Por favor, inténtelo de nuevo.');
      }
    };

    fetchSupervisorInfo();
  }, []);

  // Cargar puntos de venta disponibles para el supervisor
  useEffect(() => {
    const fetchPointsOfSale = async () => {
      try {
        setLoadingPointsOfSale(true);
        const token = localStorage.getItem('token');
        
        if (!token) {
          setError('No se encontró un token de autenticación. Por favor, inicie sesión nuevamente.');
          return;
        }

        // Obtener los puntos de venta disponibles para supervisores
        const response = await axios.get('/api/points-of-sale/available', {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        setPointsOfSale(response.data);
        
        // Si hay puntos de venta disponibles, seleccionar el primero por defecto
        if (response.data.length > 0) {
          const selectedPOS = response.data[0];
          setFormData(prevState => ({
            ...prevState,
            point_of_sale_id: selectedPOS.id
          }));
          
          // Obtener la fecha/hora del negocio si hay un punto de venta seleccionado
          if (selectedPOS.business_id) {
            console.log('Obteniendo fecha/hora del negocio para el punto de venta seleccionado...');
            const businessTime = await fetchBusinessTime(selectedPOS.business_id);
            if (businessTime) {
              setBusinessDateTime(businessTime);
              console.log('Fecha/hora del negocio establecida:', businessTime);
            }
          }
        }
      } catch (error) {
        console.error('Error al obtener puntos de venta:', error);
        setError('Error al cargar los puntos de venta. Por favor, inténtelo de nuevo.');
      } finally {
        setLoadingPointsOfSale(false);
      }
    };

    fetchPointsOfSale();
  }, []);
  
  // Efecto para actualizar la fecha/hora del negocio cuando cambia el punto de venta seleccionado
  useEffect(() => {
    const updateBusinessTime = async () => {
      if (formData.point_of_sale_id) {
        const selectedPOS = pointsOfSale.find(pos => pos.id === formData.point_of_sale_id);
        if (selectedPOS?.business_id) {
          console.log('Actualizando fecha/hora del negocio para el nuevo punto de venta...');
          const businessTime = await fetchBusinessTime(selectedPOS.business_id);
          if (businessTime) {
            setBusinessDateTime(businessTime);
            console.log('Fecha/hora del negocio actualizada:', businessTime);
          }
        }
      }
    };
    
    if (pointsOfSale.length > 0) {
      updateBusinessTime();
    }
  }, [formData.point_of_sale_id, pointsOfSale]);

  // Cargar categorías de gastos
  useEffect(() => {
    const fetchExpenseCategories = async () => {
      try {
        setLoadingCategories(true);
        const token = localStorage.getItem('token');
        
        if (!token) {
          setError('No se encontró un token de autenticación. Por favor, inicie sesión nuevamente.');
          return;
        }

        const response = await axios.get('/api/expenses/categories', {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        setExpenseCategories(response.data);
      } catch (error) {
        console.error('Error al obtener categorías de gastos:', error);
        setError('Error al cargar las categorías de gastos. Por favor, inténtelo de nuevo.');
      } finally {
        setLoadingCategories(false);
      }
    };

    fetchExpenseCategories();
  }, []);

  // Cargar tipos de gastos cuando cambia la categoría
  useEffect(() => {
    const fetchExpenseTypes = async () => {
      if (!formData.expense_category) return;
      
      try {
        setLoadingExpenseTypes(true);
        const token = localStorage.getItem('token');
        
        if (!token) {
          setError('No se encontró un token de autenticación. Por favor, inicie sesión nuevamente.');
          return;
        }

        const response = await axios.get(`/api/expenses/types/${formData.expense_category}`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        setExpenseTypes(response.data);
        
        // Limpiar el tipo de gasto seleccionado cuando cambia la categoría
        setFormData(prevState => ({
          ...prevState,
          expense_type_id: ''
        }));
      } catch (error) {
        console.error('Error al obtener tipos de gastos:', error);
        setError('Error al cargar los tipos de gastos. Por favor, inténtelo de nuevo.');
      } finally {
        setLoadingExpenseTypes(false);
      }
    };

    fetchExpenseTypes();
  }, [formData.expense_category]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        setError('No se encontró un token de autenticación. Por favor, inicie sesión nuevamente.');
        return;
      }

      // Validar campos requeridos
      if (!formData.expense_date || !formData.expense_amount || !formData.point_of_sale_id || 
          !formData.expense_type_id || !formData.payment_method) {
        setError('Todos los campos marcados con * son obligatorios');
        setLoading(false);
        return;
      }

      // Validar que el monto sea un número positivo
      if (isNaN(formData.expense_amount) || parseFloat(formData.expense_amount) <= 0) {
        setError('El monto debe ser un número positivo');
        setLoading(false);
        return;
      }
      
      // Obtener fecha/hora del negocio actualizada antes de enviar el gasto
      let finalExpenseDate = formData.expense_date;
      const selectedPointOfSale = pointsOfSale.find(pos => pos.id === formData.point_of_sale_id);
      
      if (selectedPointOfSale?.business_id) {
        console.log('Obteniendo fecha/hora actualizada del negocio para el gasto...');
        const freshBusinessTime = await fetchBusinessTime(selectedPointOfSale.business_id);
        
        if (freshBusinessTime && formData.expense_date) {
          try {
            const [userYear, userMonth, userDay] = formData.expense_date.split('-').map(Number);
            const businessDate = new Date(freshBusinessTime);
            
            const combinedDate = new Date(Date.UTC(
              userYear,
              userMonth - 1,
              userDay,
              businessDate.getUTCHours(),
              businessDate.getUTCMinutes(),
              businessDate.getUTCSeconds(),
              businessDate.getUTCMilliseconds()
            ));
            
            finalExpenseDate = combinedDate.toISOString();
          } catch (dateError) {
            console.error('Error al combinar fecha del usuario con zona horaria del negocio:', dateError);
          }
        } else {
          console.warn('No se pudo obtener la fecha/hora actualizada del negocio, usando fecha seleccionada por el usuario');
        }
      }

      // Enviar datos al servidor con la fecha combinada usando el endpoint específico del supervisor
      await axios.post('/api/supervisors/expenses', 
        {
          expense_date: finalExpenseDate,
          expense_amount: parseFloat(formData.expense_amount),
          expense_description: formData.expense_description,
          payment_method: formData.payment_method,
          point_of_sale_id: formData.point_of_sale_id,
          expense_type_id: formData.expense_type_id
        }, 
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      // Limpiar el formulario
      setFormData({
        expense_date: new Date().toISOString().split('T')[0],
        expense_amount: '',
        expense_description: '',
        payment_method: 'efectivo'.toLowerCase(),
        point_of_sale_id: formData.point_of_sale_id,
        expense_category: formData.expense_category,
        expense_type_id: ''
      });

      // Notificar al componente padre
      onExpenseAdded();
    } catch (error) {
      console.error('Error al registrar el gasto:', error);
      setError(error.response?.data?.message || 'Error al registrar el gasto. Por favor, inténtelo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  // Determinar si el selector de POS debe estar deshabilitado
  const isPOSDisabled = () => {
    if (!supervisorInfo) return true;
    // Si es supervisor específico (no general), el POS está fijo
    return !supervisorInfo.is_general_supervisor;
  };

  // Obtener el mensaje para mostrar debajo del selector de POS
  const getPOSMessage = () => {
    if (!supervisorInfo) return '';
    if (supervisorInfo.is_general_supervisor) {
      return 'Como supervisor general, puedes registrar gastos en cualquier punto de venta activo del negocio.';
    } else {
      return `Como supervisor específico, solo puedes registrar gastos en tu punto de venta asignado: ${supervisorInfo.pos_name || 'N/A'}.`;
    }
  };

  return (
    <div className="expense-form-container">
      <h3>Registrar Gasto - Supervisor</h3>
      {error && <div className="error-message">{error}</div>}
      <form className="expense-form" onSubmit={handleSubmit}>
        {/* Selector de punto de venta */}
        <div className="form-group">
          <label htmlFor="point_of_sale_id">Punto de Venta *</label>
          <select
            id="point_of_sale_id"
            name="point_of_sale_id"
            value={formData.point_of_sale_id}
            onChange={handleChange}
            disabled={loadingPointsOfSale || isPOSDisabled()}
            required
          >
            <option value="">Seleccionar punto de venta</option>
            {pointsOfSale.map(pos => (
              <option key={pos.id} value={pos.id}>
                {pos.pos_name}
              </option>
            ))}
          </select>
          {loadingPointsOfSale && <div className="loading-indicator">Cargando puntos de venta...</div>}
          {supervisorInfo && (
            <div className="info-message" style={{ fontSize: '0.9em', color: '#666', marginTop: '5px' }}>
              {getPOSMessage()}
            </div>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="expense_date">Fecha del Gasto *</label>
          <input
            type="date"
            id="expense_date"
            name="expense_date"
            value={formData.expense_date}
            onChange={handleChange}
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="expense_category">Categoría de Gasto *</label>
          <select
            id="expense_category"
            name="expense_category"
            value={formData.expense_category}
            onChange={handleChange}
            required
            disabled={loadingCategories}
          >
            <option value="">Seleccione una categoría</option>
            {expenseCategories.map((category, index) => (
              <option key={index} value={category}>
                {category}
              </option>
            ))}
          </select>
          {loadingCategories && <div className="loading-indicator">Cargando categorías...</div>}
        </div>
        <div className="form-group">
          <label htmlFor="expense_type_id">Tipo de Gasto *</label>
          <select
            id="expense_type_id"
            name="expense_type_id"
            value={formData.expense_type_id}
            onChange={handleChange}
            required
            disabled={loadingExpenseTypes || !formData.expense_category}
          >
            <option value="">Seleccione un tipo de gasto</option>
            {expenseTypes.map(type => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))}
          </select>
          {loadingExpenseTypes && <div className="loading-indicator">Cargando tipos de gastos...</div>}
        </div>
        <div className="form-group">
          <label htmlFor="expense_description">Descripción del Gasto</label>
          <textarea
            id="expense_description"
            name="expense_description"
            value={formData.expense_description}
            onChange={handleChange}
            placeholder="Descripción detallada del gasto (opcional)"
            rows="3"
          />
        </div>
        <div className="form-group">
          <label htmlFor="expense_amount">Monto del Gasto *</label>
          <div className="input-with-icon">
            <span className="currency-symbol">$</span>
            <input
              type="number"
              id="expense_amount"
              name="expense_amount"
              value={formData.expense_amount}
              onChange={handleChange}
              step="0.01"
              min="0.01"
              placeholder="0.00"
              required
              style={{ paddingLeft: '25px' }}
            />
          </div>
        </div>
        <div className="form-group">
          <label htmlFor="payment_method">Método de Pago *</label>
          <div className="payment-method-options">
            <div className={`payment-option ${formData.payment_method === 'efectivo' ? 'selected' : ''}`}>
              <input
                type="radio"
                id="payment_cash"
                name="payment_method"
                value="efectivo"
                checked={formData.payment_method === 'efectivo'}
                onChange={handleChange}
                required
              />
              <label htmlFor="payment_cash">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="6" width="20" height="12" rx="2" />
                  <circle cx="12" cy="12" r="2" />
                  <path d="M6 12h.01M18 12h.01" />
                </svg>
                Efectivo
              </label>
            </div>
            <div className={`payment-option ${formData.payment_method === 'tarjeta' ? 'selected' : ''}`}>
              <input
                type="radio"
                id="payment_card"
                name="payment_method"
                value="tarjeta"
                checked={formData.payment_method === 'tarjeta'}
                onChange={handleChange}
              />
              <label htmlFor="payment_card">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
                  <line x1="1" y1="10" x2="23" y2="10" />
                </svg>
                Tarjeta
              </label>
            </div>
            <div className={`payment-option ${formData.payment_method === 'transferencia' ? 'selected' : ''}`}>
              <input
                type="radio"
                id="payment_transfer"
                name="payment_method"
                value="transferencia"
                checked={formData.payment_method === 'transferencia'}
                onChange={handleChange}
              />
              <label htmlFor="payment_transfer">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="1" x2="12" y2="23" />
                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                  <polyline points="16 3 12 7 8 3" />
                </svg>
                Transferencia
              </label>
            </div>
          </div>
        </div>
        <div className="form-buttons">
          <button type="button" className="cancel-btn" onClick={onCancel} disabled={loading}>
            Cancelar
          </button>
          <button type="submit" className="submit-btn" disabled={loading}>
            {loading ? 'Registrando...' : 'Registrar Gasto'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SupervisorExpenseForm;
